import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IntakeFormShape } from './types';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-green-50 text-green-700',
  archived: 'bg-slate-50 text-slate-400',
};

export function IntakeFormsListRoute() {
  const getToken = useSessionTokenGetter();
  const [forms, setForms] = useState<IntakeFormShape[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/intake-forms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        if (!cancelled) setForms(json.forms as IntakeFormShape[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const visibleForms = forms.filter((f) => f.status !== 'archived');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Intake Forms</h2>
          <p className="mt-1 text-sm text-slate-500">
            Build custom questionnaires for business applicants.
          </p>
        </div>

        {/* New form dropdown */}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setTemplateMenuOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            + New Form
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>

          {templateMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setTemplateMenuOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setTemplateMenuOpen(false);
                }}
                aria-hidden="true"
              />
              <div className="absolute right-0 z-20 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
                <Link
                  to="/forms/new"
                  onClick={() => setTemplateMenuOpen(false)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">Blank form</p>
                    <p className="text-xs text-slate-500">Start from scratch</p>
                  </div>
                </Link>
                <Link
                  to="/forms/new?template=manufacturing"
                  onClick={() => setTemplateMenuOpen(false)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">Manufacturing</p>
                    <p className="text-xs text-slate-500">8 pre-built fields</p>
                  </div>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!loading && !error && visibleForms.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No intake forms yet.</p>
          <p className="mt-2">
            <Link
              to="/forms/new?template=manufacturing"
              className="text-sm font-medium text-slate-900 hover:underline"
            >
              Start with the Manufacturing template →
            </Link>
          </p>
        </div>
      )}

      {!loading && !error && visibleForms.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Name</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Fields</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleForms.map((form) => (
                <tr key={form.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <Link to={`/forms/${form.id}/edit`} className="hover:underline">
                      {form.name}
                    </Link>
                    {form.templateSlug && (
                      <span className="ml-2 text-xs text-slate-400">({form.templateSlug})</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[form.status] ?? 'bg-slate-100 text-slate-600'}`}
                    >
                      {STATUS_LABEL[form.status] ?? form.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{form.fields.length}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(form.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <Link
                        to={`/forms/${form.id}/edit`}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                      >
                        Edit
                      </Link>
                      <Link
                        to={`/forms/${form.id}/submissions`}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                      >
                        Submissions
                      </Link>
                      <Link
                        to={`/forms/${form.id}/preview`}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                      >
                        Preview →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
            {visibleForms.length} form{visibleForms.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}
